"use client";

import { Fragment } from "react";

import {
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";
import { useNoteLabel } from "@/hooks/use-notation";

/**
 * Un accord dit en toutes lettres : `Ré · Fa · La`, les doigtés des deux mains
 * dessous, et chaque nom qui s'allume quand la note est prise.
 *
 * C'est **le** dessin d'accord du produit, celui de l'écran d'exercice
 * d'accords, et il est ici pour que le parcours harmonique le montre à
 * l'identique plutôt que d'en tenir une copie. Deux dessins du même objet
 * finissent toujours par diverger d'une graisse, d'un violet ou d'un délai, et
 * le joueur qui passe d'un écran à l'autre paie la différence sans jamais
 * pouvoir la nommer.
 *
 * ## Il ne sait rien du domaine, et c'est ce qui le rend partageable
 *
 * Il ne connaît ni classe de hauteur, ni touche MIDI, ni renversement, ni
 * niveau. Il reçoit des notes déjà décidées : leur nom, laquelle est la
 * fondamentale, laquelle est prise, et ce que chaque main y fait. Les deux
 * appelants répondent à ces questions par des chemins qui n'ont rien à voir —
 * l'exercice compare des classes de hauteur à l'octave près et joue d'une main,
 * le parcours compare des touches exactes et en joue deux — et c'est
 * précisément pourquoi le dessin ne doit pas les connaître.
 *
 * C'est ce qui distingue ce composant de `ChordPracticeGridStage`, dont il est
 * extrait : celui-là traîne `onSwitchToTempo`, `maxInvIdx`, `prevTitle`,
 * `onRandom`, c'est-à-dire la plomberie d'un exercice. Partager cette
 * plomberie-là était l'erreur à ne pas refaire ; partager le dessin était
 * l'inverse.
 *
 * ## Les doigtés tombent sous leur note
 *
 * Une seule grille tient les trois rangées, séparateurs compris, donc un
 * chiffre est toujours sous la note qu'il concerne. La version d'origine
 * empilait deux rangées flex indépendantes, avec un pas fixe entre les
 * chiffres : ça tenait tant que toutes les notes faisaient la même largeur, et
 * ça glissait dès que `Fa♯` en côtoyait `Si`.
 *
 * ## L'entrée
 *
 * La cascade tombe note après note. `animation-fill-mode: backwards` et jamais
 * `both` : un élément transformé devient le bloc conteneur de ses descendants
 * en `position: fixed`, et ces deux écrans portent des couches flottantes
 * au-dessus d'eux. La classe apporte son propre `prefers-reduced-motion` dans
 * la feuille globale, qui n'a pas de garde générale.
 *
 * ## Ce que l'appelant doit fournir
 *
 * Un ancêtre en `container-type: size`, sans quoi les `cqw` / `cqh` valent zéro
 * et les lettres avec.
 *
 * La largeur sur laquelle le dessin se règle est celle de ce conteneur, sauf
 * quand l'appelant la nomme par `measure`. Ce n'est pas un réglage de goût : la
 * mesure était déjà la sienne, il la donnait par la largeur de la boîte qu'il
 * fournissait. La dire plutôt que l'imposer est ce qui libère la boîte, car une
 * boîte qui porte la mesure ne peut pas épouser le dessin : elle est plus large
 * que lui par construction, le dessin s'y centre, et il reste du mou de chaque
 * côté. Un appelant qui pose quelque chose contre la première lettre paie ce
 * mou-là.
 */

/** Ce qu'une main fait sur une note de l'accord. */
export type BigChordFinger = {
  /** Le doigt, ou `null` quand aucune source n'en a posé. */
  readonly finger: number | null;
  /** Sa touche est enfoncée. */
  readonly down: boolean;
};

/** Une note de l'accord, telle que le dessin la demande. */
export type BigChordNote = {
  /** Identité stable de la colonne, pour le rendu de liste. */
  readonly id: string;
  /**
   * Le nom, dans l'orthographe de l'accord. La notation du joueur est
   * appliquée ici, par `useNoteLabel` : personne d'autre ne réécrit la
   * convention.
   */
  readonly name: string;
  /** C'est la fondamentale : elle s'accentue en violet. */
  readonly root: boolean;
  /**
   * La note est prise.
   *
   * Ce que « prise » veut dire appartient à l'appelant, et les deux ne
   * répondent pas pareil : l'exercice accepte n'importe quelle octave, le
   * parcours demande toutes les touches de la note, les deux mains comprises.
   */
  readonly held: boolean;
  readonly right: BigChordFinger | null;
  readonly left: BigChordFinger | null;
};

/**
 * Part de la hauteur du conteneur qu'une note a le droit de prendre.
 *
 * Le seul réglage du composant, et il est géométrique : c'est la forme de la
 * boîte qui le décide, pas le goût de l'appelant. Un panneau haut et étroit
 * s'en tient à 40 % sous peine de pousser les chiffres hors champ ; une bande
 * large et basse laisserait sinon un tiers de sa hauteur vide.
 *
 * Tout le reste — bornes de largeur, rapport entre la note et son séparateur,
 * graisses, interlettrage — en découle et ne se règle pas.
 */
export type BigChordHeightCap = 40 | 52;

export function BigChordNotes({
  notes,
  resetKey,
  heightCap = 40,
  measure = "100cqw",
  fingers = true,
  ariaLabel,
}: {
  readonly notes: readonly BigChordNote[];
  /**
   * Change quand l'accord change, ce qui rejoue la cascade.
   *
   * C'est un accord nouveau, pas le même accord dont les lettres auraient
   * changé, et le remontage est la façon la plus simple de le dire.
   */
  readonly resetKey: string;
  readonly heightCap?: BigChordHeightCap;
  /**
   * La longueur sur laquelle toutes les parts horizontales se lisent.
   *
   * `100cqw` par défaut, c'est-à-dire la boîte du dessin : rien ne change pour
   * qui n'en dit rien. L'appelant qui a besoin que la boîte épouse le dessin la
   * nomme ici, et la boîte n'a plus à la porter.
   *
   * L'axe vertical, lui, se lit toujours sur le conteneur : les deux axes ne
   * cherchent pas le même repère, et c'était déjà vrai avant.
   */
  readonly measure?: string;
  /**
   * Les deux rangées de doigtés, MD au-dessus de MG.
   *
   * L'écran d'exercice d'accords les garde : là, le doigté **est** l'exercice,
   * et rien d'autre ne le dit. Le parcours harmonique les a retirées : elles y
   * coûtaient deux lignes de hauteur sur un écran qui en manque, et le clavier
   * juste dessous montre déjà les touches allumées, main par main, avec la
   * couleur de chacune.
   *
   * Le point sous la colonne reste dans les deux cas : c'est lui qui dit que la
   * note est prise, et il ne coûte rien.
   */
  readonly fingers?: boolean;
  readonly ariaLabel: string;
}) {
  const toNoteLabel = useNoteLabel();

  if (notes.length === 0) {
    return null;
  }

  // Au-delà de trois notes tout rétrécit d'un quart : c'est le seul autre
  // paramètre du dessin, et il se lit sur le contenu, pas sur l'appelant.
  const wide = notes.length > 3;
  const cap = wide ? Math.round(heightCap * 0.75) : heightCap;
  const separatorCap = wide ? Math.round(heightCap * 0.4) : Math.round(heightCap * 0.55);
  /*
    Sans les doigtés, les lettres reprennent la hauteur que les chiffres
    laissent : deux rangées de moins sous elles, donc de la place pour la même
    composition en plus grand. C'est encore la géométrie de la boîte qui décide
    et pas le goût de l'appelant, ce qui est la règle du composant.
  */
  const grow = (size: string) => (fingers ? size : `calc(${size} * 1.15)`);
  // Une part de la mesure, et la mesure est la boîte tant qu'on n'en dit rien :
  // `calc(100cqw * 0.13)` est le `13cqw` d'avant, au centième près.
  const share = (part: number) => `calc(${measure} * ${part})`;
  const nameSize = grow(
    wide ? `min(clamp(40px, ${share(0.1)}, 110px), ${cap}cqh)` : `min(clamp(48px, ${share(0.13)}, 160px), ${cap}cqh)`,
  );
  const separatorSize = grow(
    wide
      ? `min(clamp(22px, ${share(0.06)}, 60px), ${separatorCap}cqh)`
      : `min(clamp(28px, ${share(0.07)}, 84px), ${separatorCap}cqh)`,
  );

  /*
    Une colonne de grille par cellule, séparateurs compris, précédée de celle
    des légendes `MD` / `MG`.

    Cette colonne-là ne vit que pour les doigtés : sans eux elle ne porte plus
    rien, et une colonne vide en tête décale tout le dessin d'une gouttière vers
    la droite par rapport au centre de sa boîte. L'appelant qui pose ce dessin
    sous un nom centré voyait alors ses lettres passer à côté de l'axe du nom.
  */
  const template = [
    ...(fingers ? ["auto"] : []),
    ...notes.flatMap((_, at) => (at === 0 ? ["auto"] : ["auto", "auto"])),
  ].join(" ");

  return (
    <div
      key={resetKey}
      role="group"
      aria-label={ariaLabel}
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: template,
        alignItems: "center",
        justifyItems: "center",
        columnGap: `clamp(8px, ${share(0.012)}, 16px)`,
        rowGap: 0,
      }}
    >
      {/* Rangée des noms. */}
      {fingers && <span aria-hidden />}
      {notes.map((note, at) => (
        <Fragment key={`name:${note.id}`}>
          {at > 0 && <Separator size={separatorSize} />}
          <span
            className="chord-note-drop"
            style={{
              /*
                La ligne de base que le dessin publie est celle des lettres.

                Une grille dont aucun élément de la première rangée n'est aligné
                sur la ligne de base a le droit d'en synthétiser une sur son
                bord, c'est-à-dire ici sous les pastilles. Les navigateurs
                lisent aujourd'hui les lettres, mais rien ne les y oblige, et le
                parcours harmonique assied sa basse sur cette ligne-là.

                Sans effet sur le dessin : les lettres ont toutes le même corps,
                donc la rangée fait leur hauteur et les aligner par leur ligne
                de base ou par leur milieu les pose au même endroit. Le
                séparateur, lui, garde son milieu.
              */
              alignSelf: "baseline",
              fontFamily: "Inter, sans-serif",
              fontSize: nameSize,
              fontWeight: 200,
              lineHeight: 0.9,
              letterSpacing: "-0.06em",
              animationDelay: `${at * 110}ms`,
              /*
                La fondamentale en violet, le reste en blanc qui s'éteint vers
                le bas : c'est ce qui fait lire le nom de l'accord dans la file
                des notes. Prise, la lettre cesse de s'éteindre : c'est le
                dégradé qui allume, le halo ne fait que déborder.

                `backgroundImage` et surtout pas le raccourci `background` :
                celui-ci porte aussi `background-clip`, donc le réécrire à
                chaud le remet à `border-box`. La lettre perdait alors son
                découpage au texte et se peignait en bloc plein, mais
                seulement à l'instant où on la jouait, puisque c'est le seul
                moment où ce dégradé change.
              */
              backgroundImage: note.root
                ? `linear-gradient(180deg, ${VIOLET_BRIGHT} 0%, ${note.held ? VIOLET_BRIGHT : VIOLET} 100%)`
                : `linear-gradient(180deg, ${TEXT} 0%, ${note.held ? TEXT : TEXT_DIM} 90%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              transition: "filter 220ms ease",
              filter: note.held ? heldGlow(note.root) : NO_GLOW,
            }}
          >
            {toNoteLabel(note.name)}
          </span>
        </Fragment>
      ))}

      {/* MD au-dessus de MG, dans l'ordre où les mains sont sur le clavier vu
          de face. */}
      {fingers && (
        <>
          <HandRow label="MD" notes={notes} hand="right" resting={TEXT} />
          <HandRow label="MG" notes={notes} hand="left" resting={TEXT_DIM} />
        </>
      )}

      {/* Le point sous la colonne : la note entière est prise. */}
      {fingers && <span aria-hidden />}
      {notes.map((note, at) => (
        <Fragment key={`dot:${note.id}`}>
          {at > 0 && <span aria-hidden />}
          <span
            aria-hidden
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              marginTop: 6,
              background: note.held ? VIOLET_BRIGHT : "transparent",
              border: `1px solid ${note.held ? VIOLET : "rgba(255,255,255,0.12)"}`,
              boxShadow: note.held ? `0 0 8px ${VIOLET_GLOW}` : "none",
              transition: "background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
            }}
          />
        </Fragment>
      ))}
    </div>
  );
}

/** Rien à voir, et pas « pas de filtre » : il faut la même forme pour transiter. */
const NO_GLOW = "drop-shadow(0 0 0 rgba(139, 92, 246, 0))";

/**
 * Le halo d'une note prise.
 *
 * Deux halos et pas un, parce qu'une seule couleur ne peut pas éclairer les
 * deux lettres. Le violet du produit déborde bien d'une lettre blanche et
 * disparaît sur une lettre violette : sur la fondamentale, jouer la note ne se
 * voyait pas. Elle reçoit donc un halo clair, qui la détache de sa propre
 * teinte au lieu de s'y fondre.
 */
function heldGlow(root: boolean): string {
  if (root) {
    return "drop-shadow(0 0 20px rgba(196, 181, 253, 0.6)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.45))";
  }

  return `drop-shadow(0 0 18px ${VIOLET_GLOW}) drop-shadow(0 0 6px rgba(196, 181, 253, 0.35))`;
}

/**
 * Ce qui sépare deux choses écrites en grand, dans la graisse et le gris des
 * lettres qu'il sépare.
 *
 * Exporté parce que le parcours harmonique en a besoin **hors** de cette
 * grille : la basse est posée à gauche de l'accord, derrière un séparateur, et
 * en dessiner un second là-bas ferait diverger deux marques d'une seule chose.
 *
 * Le glyphe se choisit, et il porte tout le sens : le point relie des notes
 * d'un même accord, la barre sépare deux objets de nature différente. Écrire la
 * basse derrière un point la ferait lire comme une quatrième note de la triade,
 * ce qui est exactement l'erreur dont elle sort.
 */
/**
 * Le signe qui sépare deux noms.
 *
 * Un point bas et non un point médian, et ce n'est pas une nuance : mesurée
 * dans Inter, l'encre du point médian se peint entre 30,3 % et 38,1 % du
 * cadratin **au-dessus** de la ligne de base. Elle flotte donc à mi-hauteur des
 * lettres quoi qu'on aligne, puisque c'est le dessin du glyphe qui la place et
 * non sa mise en page. Le point bas s'assied sur la ligne de base par
 * construction, dans toutes les polices et sans constante à régler.
 *
 * Et c'est le même rond : 7,85 % du cadratin de haut contre 7,81 % pour le
 * médian, à quatre centièmes près. Seule sa hauteur change.
 */
const SEPARATOR_GLYPH = ".";

export function Separator({ size, glyph = SEPARATOR_GLYPH }: { readonly size: string; readonly glyph?: string }) {
  return (
    <span
      aria-hidden
      style={{
        fontFamily: "Inter, sans-serif",
        fontSize: size,
        fontWeight: 200,
        lineHeight: 0.9,
        color: TEXT_MUTE,
        /*
          Sur la ligne de base du texte, comme les lettres qu'il sépare.

          La grille qui le pose aligne ses cellules par le milieu, et un
          séparateur centré dans une rangée à la hauteur des lettres publie une
          ligne de base à neuf pixels de la leur, pour un corps de 55,5. Il faut
          les deux pour que l'encre tombe au bon endroit : cette ligne-ci, et un
          glyphe qui s'y assied. Voir SEPARATOR_GLYPH.
        */
        alignSelf: "baseline",
      }}
    >
      {glyph}
    </span>
  );
}

/**
 * Une rangée de doigtés, avec sa légende.
 *
 * Rendue en fragment plutôt qu'en conteneur : les cellules appartiennent à la
 * grille du dessus, et les emballer dans une `div` casserait l'alignement
 * qu'elles existent pour tenir.
 */
function HandRow({
  label,
  notes,
  hand,
  resting,
}: {
  readonly label: string;
  readonly notes: readonly BigChordNote[];
  readonly hand: "left" | "right";
  /** La main droite passe devant la gauche. */
  readonly resting: string;
}) {
  return (
    <>
      <span
        aria-hidden
        style={{
          justifySelf: "end",
          fontFamily: FONT_MONO,
          fontSize: 9,
          lineHeight: "16px",
          letterSpacing: "0.08em",
          color: TEXT_MUTE,
        }}
      >
        {label}
      </span>

      {notes.map((note, at) => {
        const side = note[hand];

        return (
          <Fragment key={`${hand}:${note.id}`}>
            {at > 0 && <span aria-hidden />}
            <span
              aria-hidden
              style={{
                fontFamily: FONT_MONO,
                fontSize: 13,
                fontWeight: 500,
                lineHeight: "16px",
                transition: "color 200ms ease, text-shadow 200ms ease",
                color: side?.down ? VIOLET_BRIGHT : resting,
                textShadow: side?.down ? `0 0 10px ${VIOLET_GLOW}` : "none",
              }}
            >
              {side?.finger ?? ""}
            </span>
          </Fragment>
        );
      })}
    </>
  );
}
